import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  BarChart3, 
  Users, 
  ShoppingBag, 
  FileText, 
  MessageSquare, 
  Heart, 
  Palette, 
  Settings, 
  TrendingUp, 
  Eye, 
  Plus,
  Calendar,
  DollarSign,
  Package,
  UserCheck,
  Mail,
  Star,
  Target,
  Activity,
  Image
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTenantContext } from '../../context/TenantContext';
import { formatCurrency } from '../../utils/formatters';
import { useToast } from '../../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  blog: {
    totalPosts: number;
    publishedPosts: number;
    draftPosts: number;
    totalViews: number;
  };
  shop: {
    totalProducts: number;
    totalOrders: number;
    totalRevenue: number;
    pendingOrders: number;
  };
  portfolio: {
    totalArtworks: number;
    totalViews: number;
    featuredArtworks: number;
  };
  crowdfunding: {
    totalCampaigns: number;
    activeCampaigns: number;
    totalRaised: number;
    totalContributions: number;
  };
  messages: {
    totalMessages: number;
    unreadMessages: number;
    todayMessages: number;
  };
  partners: {
    totalPartners: number;
    activePartners: number;
  };
}

export default function Dashboard() {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, [tenant]);

  const fetchDashboardStats = async () => {
    if (!tenant) return;
    
    try {
      setLoading(true);
      
      // Utiliser uniquement VITE_TENANT_ID comme source de vérité
      const tenantId = import.meta.env.VITE_TENANT_ID || '2';
      console.log('Dashboard utilise tenant_id:', tenantId);
      
      // Récupérer les statistiques du blog
      const { data: blogPosts } = await supabase
        .from('blog_posts')
        .select('id, status, views_count')
        .eq('tenant_id', tenantId);
      
      // Récupérer les statistiques de la boutique
      const { data: products } = await supabase
        .from('shop_products')
        .select('id')
        .eq('tenant_id', tenantId);
      
      const { data: orders } = await supabase
        .from('shop_orders')
        .select('id, status, total_amount')
        .eq('tenant_id', tenantId);
      
      // Récupérer les statistiques du portfolio
      const { data: artworks, error: artworksError } = await supabase
        .from('portfolio_artworks')
        .select('id, status')
        .eq('tenant_id', tenantId);
      
      if (artworksError) {
        console.error('Erreur portfolio:', artworksError);
      }
      
      // Récupérer les statistiques du crowdfunding
      const { data: campaigns } = await supabase
        .from('crowdfunding_campaigns')
        .select('id, status, goal_amount, collected_amount')
        .eq('tenant_id', tenantId);
      
      const { data: contributions } = await supabase
        .from('contributions')
        .select('id, amount')
        .eq('tenant_id', tenantId)
        .eq('payment_status', 'paid');
      
      // Récupérer les statistiques des messages
      const { data: messages } = await supabase
        .from('contact_messages')
        .select('id, read, created_at')
        .eq('tenant_id', tenantId);
      
      // Récupérer les statistiques des partenaires
      const { data: partners, error: partnersError } = await supabase
        .from('partners')
        .select('*')
        .eq('tenant_id', tenantId);
        
      if (partnersError) {
        console.error('Erreur partenaires:', partnersError);
      }
      
      // Log pour déboguer
      console.log('Blog posts:', blogPosts);
      console.log('Products:', products);
      console.log('Orders:', orders);
      console.log('Artworks:', artworks);
      console.log('Partners:', partners);
      
      // Calculer les statistiques
      const dashboardStats: DashboardStats = {
        blog: {
          totalPosts: blogPosts?.length || 0,
          publishedPosts: blogPosts?.filter(p => p.status === 'published').length || 0,
          draftPosts: blogPosts?.filter(p => p.status === 'draft').length || 0,
          totalViews: blogPosts?.reduce((sum, p) => sum + (p.views_count || 0), 0) || 0
        },
        shop: {
          totalProducts: products?.length || 0,
          totalOrders: orders?.length || 0,
          totalRevenue: orders?.reduce((sum, o) => sum + (o.total_amount || 0), 0) || 0,
          pendingOrders: orders?.filter(o => o.status === 'pending').length || 0
        },
        portfolio: {
          totalArtworks: artworks?.length || 0,
          totalViews: 0, // La colonne views_count n'existe pas dans la table
          featuredArtworks: artworks?.filter(a => a.status === 'featured' || a.status === 'published').length || 0
        },
        crowdfunding: {
          totalCampaigns: campaigns?.length || 0,
          activeCampaigns: campaigns?.filter(c => c.status === 'active').length || 0,
          totalRaised: campaigns?.reduce((sum, c) => sum + (c.collected_amount || 0), 0) || 0,
          totalContributions: contributions?.length || 0
        },
        messages: {
          totalMessages: messages?.length || 0,
          unreadMessages: messages?.filter(m => !m.read).length || 0,
          todayMessages: messages?.filter(m => {
            const today = new Date().toDateString();
            return new Date(m.created_at).toDateString() === today;
          }).length || 0
        },
        partners: {
          totalPartners: partners?.length || 0,
          activePartners: partners?.length || 0 // Tous les partenaires sont considérés comme actifs car il n'y a pas de colonne status
        }
      };
      
      setStats(dashboardStats);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les statistiques du tableau de bord",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    {
      title: 'Blog',
      description: 'Gérer les articles et catégories',
      icon: FileText,
      color: 'bg-blue-500',
      link: '/admin/blog',
      stats: stats ? [
        { label: 'Articles', value: stats.blog.totalPosts },
        { label: 'Publiés', value: stats.blog.publishedPosts },
        { label: 'Vues', value: stats.blog.totalViews }
      ] : []
    },
    {
      title: 'Boutique',
      description: 'Produits, commandes et ventes',
      icon: ShoppingBag,
      color: 'bg-green-500',
      link: '/admin/boutique',
      stats: stats ? [
        { label: 'Produits', value: stats.shop.totalProducts },
        { label: 'Commandes', value: stats.shop.totalOrders },
        { label: 'Revenus', value: formatCurrency(stats.shop.totalRevenue) }
      ] : []
    },
    {
      title: 'Portfolio',
      description: 'Œuvres d\'art et galeries',
      icon: Palette,
      color: 'bg-purple-500',
      link: '/admin/portfolio',
      stats: stats ? [
        { label: 'Œuvres', value: stats.portfolio.totalArtworks },
        { label: 'Vues', value: stats.portfolio.totalViews },
        { label: 'En vedette', value: stats.portfolio.featuredArtworks }
      ] : []
    },
    {
      title: 'Crowdfunding',
      description: 'Campagnes de financement',
      icon: Heart,
      color: 'bg-red-500',
      link: '/admin/crowdfunding',
      stats: stats ? [
        { label: 'Campagnes', value: stats.crowdfunding.totalCampaigns },
        { label: 'Actives', value: stats.crowdfunding.activeCampaigns },
        { label: 'Collecté', value: formatCurrency(stats.crowdfunding.totalRaised) }
      ] : []
    },
    {
      title: 'Messages',
      description: 'Messages et communications',
      icon: MessageSquare,
      color: 'bg-yellow-500',
      link: '/admin/messages',
      stats: stats ? [
        { label: 'Total', value: stats.messages.totalMessages },
        { label: 'Non lus', value: stats.messages.unreadMessages },
        { label: 'Aujourd\'hui', value: stats.messages.todayMessages }
      ] : []
    },
    {
      title: 'Partenaires',
      description: 'Gestion des partenariats',
      icon: Users,
      color: 'bg-indigo-500',
      link: '/admin/partners',
      stats: stats ? [
        { label: 'Total', value: stats.partners.totalPartners },
        { label: 'Actifs', value: stats.partners.activePartners }
      ] : []
    }
  ];

  const quickActions = [
    { title: 'Nouvel article', icon: Plus, link: '/admin/blog/create', color: 'bg-blue-600' },
    { title: 'Nouveau produit', icon: Package, link: '/admin/boutique/creer', color: 'bg-green-600' },
    { title: 'Nouvelle œuvre', icon: Palette, link: '/admin/portfolio/create', color: 'bg-purple-600' },
    { title: 'Nouvelle campagne', icon: Target, link: '/admin/crowdfunding/create', color: 'bg-red-600' }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-8">
          <div className="container mx-auto py-8">
            <div className="animate-pulse">
              <div className="h-8 bg-white/10 rounded mb-4 w-64"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="h-48 bg-white/5 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-8">
        <div className="container mx-auto py-8">
          {/* En-tête */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">
                  Tableau de bord
                </h1>
                <p className="text-gray-400">
                  Bienvenue dans votre espace d'administration
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <Button 
                  onClick={fetchDashboardStats}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Actualiser
                </Button>
                <Button 
                  variant="outline" 
                  className="bg-gray-700 hover:bg-gray-600 text-white border-0"
                  onClick={() => navigate('/admin/mediatheque')}
                >
                  <Image className="mr-2 h-4 w-4" />
                  Médiathèque
                </Button>
                <Link to="/admin/configuration">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <Settings className="h-4 w-4 mr-2" />
                    Configuration
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Actions rapides</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action, index) => (
                <Link key={index} to={action.link}>
                  <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-200 cursor-pointer group">
                    <CardContent className="p-4 text-center">
                      <div className={`${action.color} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                        <action.icon className="h-6 w-6 text-white" />
                      </div>
                      <p className="text-white font-medium text-sm">{action.title}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Modules principaux */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-6">Modules</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modules.map((module, index) => (
                <Link key={index} to={module.link}>
                  <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-all duration-200 cursor-pointer group h-full">
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between">
                        <div className={`${module.color} w-12 h-12 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                          <module.icon className="h-6 w-6 text-white" />
                        </div>
                        <Eye className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
                      </div>
                      <CardTitle className="text-white text-lg">{module.title}</CardTitle>
                      <p className="text-gray-400 text-sm">{module.description}</p>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {module.stats.map((stat, statIndex) => (
                          <div key={statIndex} className="flex justify-between items-center">
                            <span className="text-gray-400 text-sm">{stat.label}</span>
                            <Badge variant="secondary" className="bg-white/10 text-white border-white/20">
                              {stat.value}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Statistiques globales */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-blue-600/20 to-blue-800/20 border-blue-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-200 text-sm font-medium">Articles publiés</p>
                      <p className="text-2xl font-bold text-white">{stats.blog.publishedPosts}</p>
                    </div>
                    <FileText className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-green-600/20 to-green-800/20 border-green-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-200 text-sm font-medium">Revenus totaux</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(stats.shop.totalRevenue)}</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-purple-600/20 to-purple-800/20 border-purple-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-200 text-sm font-medium">Œuvres d'art</p>
                      <p className="text-2xl font-bold text-white">{stats.portfolio.totalArtworks}</p>
                    </div>
                    <Palette className="h-8 w-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-red-600/20 to-red-800/20 border-red-500/30">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-red-200 text-sm font-medium">Fonds collectés</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(stats.crowdfunding.totalRaised)}</p>
                    </div>
                    <Heart className="h-8 w-8 text-red-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}